import React, { useState } from "react";
import { ArrowRight, Phone } from "lucide-react";
import { CALL_NUMBER_DISPLAY, CALL_NUMBER_TEL } from "../lib/contact";
import Reveal from "../components/Reveal";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";

const STATS = [
    ["120+", "Artists developed"],
    ["500+", "Releases delivered"],
    ["50M+", "Streams generated"],
    ["7", "Creative disciplines"],
];

const VALUES = [
    ["Art direction over decoration", "We lead with taste and restraint, every choice serves the work."],
    ["Craft over shortcuts", "From the mix to the master, we sweat the details others skip."],
    ["Artists over algorithms", "We build careers, not just moments, sustainable growth beats virality."],
];

export default function About() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const { data } = await api.post("/contact", { ...form, type: "consultation" });
            toast.success(data.message || "Thanks, we'll be in touch.");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            toast.error(apiError(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="about-page">
            <section className="bg-cream py-20 lg:py-24 border-b border-[#e8e1d4]">
                <div className="mx-container text-center">
                    <p className="overline mb-5">About MUVIARTS</p>
                    <h1 className="font-display font-semibold tracking-tight text-5xl sm:text-7xl leading-[0.95] max-w-4xl mx-auto">
                        A creative company for the <span className="italic text-brand-gold">next generation</span> of artists.
                    </h1>
                    <p className="mt-8 max-w-xl mx-auto text-neutral-500 text-lg">MUVIARTS is a premium creative agency, music company and artist development platform. We help independent artists create, release, build and grow, under one roof.</p>
                </div>
            </section>

            <section className="py-16 bg-white border-b border-neutral-200">
                <div className="mx-container grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {STATS.map(([n, l]) => (
                        <div key={l}>
                            <p className="font-display font-black text-4xl sm:text-5xl tracking-tightest">{n}</p>
                            <p className="mt-1 text-sm text-neutral-500 uppercase tracking-wide">{l}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-white">
                <div className="mx-container">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <p className="overline mb-4">What we believe</p>
                        <h2 className="font-display font-black uppercase tracking-tightest text-4xl">Our principles</h2>
                    </div>
                    <div className="max-w-3xl mx-auto divide-y divide-neutral-200 border-t border-neutral-200">
                        {VALUES.map(([t, d], i) => (
                            <Reveal key={t}>
                                <div className="py-8 flex gap-6" data-testid={`value-${i}`}>
                                    <span className="big-index text-4xl text-neutral-200">0{i + 1}</span>
                                    <div>
                                        <h3 className="font-display font-bold text-xl">{t}</h3>
                                        <p className="mt-2 text-neutral-500 leading-relaxed max-w-xl">{d}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-offwhite" id="contact">
                <div className="mx-container grid lg:grid-cols-2 gap-14 items-center">
                    <div>
                        <p className="overline mb-4">Talk to MUVIARTS</p>
                        <h2 className="font-display font-black uppercase tracking-tightest text-4xl sm:text-5xl leading-[0.95]">Book a free<br /><span className="text-brand-blue">consultation.</span></h2>
                        <p className="mt-5 text-neutral-500 max-w-md leading-relaxed">Tell us where you are and where you want to go. We'll map out the right path, no pressure, no jargon.</p>
                        <a href={CALL_NUMBER_TEL} className="mt-6 inline-flex items-center gap-2 font-semibold text-ink hover:text-brand-blue transition-colors" data-testid="contact-call">
                            <Phone className="h-4 w-4" /> Prefer to talk? Call {CALL_NUMBER_DISPLAY}
                        </a>
                    </div>
                    <form onSubmit={submit} className="bg-white border border-neutral-200 p-8" data-testid="contact-form">
                        <div className="space-y-4">
                            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-ink" />
                            <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-ink" />
                            <textarea required rows={4} placeholder="Tell us about your project" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-ink resize-none" />
                            <button disabled={sending} className="btn-primary w-full disabled:opacity-60" data-testid="contact-submit">{sending ? "Sending…" : "Send Message"} <ArrowRight className="h-4 w-4" /></button>
                        </div>
                    </form>
                </div>
            </section>
        </div>
    );
}
